import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import Pagination from "../Pagination/Pagination";
import FilterBar from "../FilterBar/FilterBar";
import { Edit, Trash2 } from "lucide-react";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import AuthService from "../../services/Auth.service";
import ApiService from "../../services/Api.service";
import AddReplacementModal from "./AddReplacementModal";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

export default function ReplacementList() {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [headers, setHeaders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [languageOptions, setLanguageOptions] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });

  const validationSchema = yup.object().shape({});
  const { handleSubmit, control } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      setLoading(true);
      const res = await AuthService.getReplacementList({ page, size });

      if (res.status === 200 && res.data) {
        setTableData(res.data.data || []);
        setPagination(res.data.pagination || {});
        setHeaders(res.data.header || []);
      } else {
        toast.error("Failed to fetch replacements list");
      }
    } catch (error) {
      console.error("Error fetching replacements:", error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguageOptions = async () => {
    try {
      const res = await ApiService.getLanguageOptions();
      if (res.status === 200 && res.data) {
        const combined = [];
        if (Array.isArray(res.data.default_language)) {
          combined.push(...res.data.default_language);
        }
        if (Array.isArray(res.data.other_languages)) {
          combined.push(...res.data.other_languages);
        }
        setLanguageOptions(combined);
      }
    } catch (error) {
      console.error("Error fetching language options:", error);
    }
  };

  useEffect(() => {
    fetchDetail();
    fetchLanguageOptions();
  }, []);

  const handleAdd = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSaveReplacement = async (data) => {
    try {
      let res;
      if (isEditing && selectedItem) {
        res = await AuthService.updateReplacement(selectedItem.id, data);
        if (res.status === 200) {
          toast.success("Replacement word updated successfully");
        } else {
          toast.error("Failed to update replacement word");
        }
      } else {
        res = await AuthService.addReplacement(data);
        if (res.status === 200 || res.status === 201) {
          toast.success("Replacement word added successfully");
        } else {
          toast.error("Failed to add replacement word");
        }
      }

      setShowModal(false);
      fetchDetail(pagination.page, postsPerPage);
    } catch (error) {
      console.error("Save error:", error);
      const msg = error.response?.data?.detail || "Failed to save replacement word";
      toast.error(msg);
    }
  };

  const handleDeleteReplacement = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await AuthService.deleteReplacement(deleteTarget.id);

      if (res.status === 200 || res.status === 204) {
        toast.success("Replacement word deleted successfully");
        fetchDetail(pagination.page, postsPerPage);
      } else {
        toast.error("Failed to delete replacement word");
      }
    } catch (error) {
      console.error("Error deleting replacement:", error);
      toast.error("Error deleting replacement word");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(pagination.page, newSize);
  };

  const handleExportCSV = async () => {
    try {
      const res = await AuthService.exportReplacements();
      
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `replacement_words_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Replacements exported successfully");
    } catch (error) {
      console.error("Error exporting replacements:", error);
      toast.error("Failed to export replacements CSV");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await AuthService.importReplacements(file);
      
      if (res.status === 200) {
        toast.success(res.data.message || "Replacements imported successfully");
        fetchDetail(1, postsPerPage);
      } else {
        toast.error("Failed to import CSV");
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      const msg = error.response?.data?.detail || "Failed to import replacements CSV";
      toast.error(msg);
    } finally {
      e.target.value = "";
    }
  };

  const mistableTh = {
    backgroundColor: "transparent",
    color: "rgba(43, 58, 103, 1)",
    fontFamily: "Nunito",
    fontSize: "13px",
    fontWeight: 700,
    textAlign: "center",
  };

  const tableTdStyles = {
    backgroundColor: "#ffffff",
    fontFamily: "Nunito",
    fontSize: "12px",
    fontWeight: "700",
    textAlign: "center",
    color: "#232122",
  };

  return (
    <>
      <div className="main-contanier">
        <FilterBar
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={() => {}}
          onClearFilters={() => fetchDetail(1, postsPerPage)}
          filters={[]}
          title="Replacements"
          totalCount={pagination.total || 0}
          tableId="replacements-table"
          isDownload={false}
          isAdd={true}
          onAdd={handleAdd}
          isImportCsv={true}
          onImportCsv={handleImportCSV}
          isExportCsv={true}
          onExportCsv={handleExportCSV}
        />

        <div className="table-container">
          {loading ? (
            <SkeletonLoader type="table" rows={8} cols={5} />
          ) : (
            <table id="replacements-table" className="custom-table-container">
              <thead
                className="table-head"
                style={{ position: "sticky", top: "0", zIndex: "1" }}
              >
                <tr>
                  <th style={mistableTh}>S.No</th>
                  {headers.map((header, i) => (
                    <th key={i} style={mistableTh}>
                      {header.label}
                    </th>
                  ))}
                  <th style={mistableTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableTdStyles}>{(pagination.page - 1) * postsPerPage + index + 1}</td>
                      {headers.map((header, i) => (
                        <td key={i} style={tableTdStyles}>
                          {item[header.value] ?? "--"}
                        </td>
                      ))}

                      <td style={tableTdStyles}>
                        <button
                          className="icon-btn edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit Replacement Word"
                        >
                          <Edit size={18} color="#f4a261" />
                        </button>

                        <button
                          className="icon-btn delete-btn"
                          onClick={() => handleDeleteReplacement(item)}
                          title="Delete Replacement Word"
                          style={{ marginLeft: "8px" }}
                        >
                          <Trash2 size={18} color="#e63946" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={headers.length + 2} style={tableTdStyles}>
                      No replacements defined.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add / Edit Modal */}
        {showModal && (
          <AddReplacementModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onSave={handleSaveReplacement}
            itemData={isEditing ? selectedItem : null}
            languageOptions={languageOptions}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <ConfirmDeleteModal
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}
            onConfirm={handleConfirmDelete}
            itemName={deleteTarget?.target || "this replacement word"}
          />
        )}

        <Pagination
          pagination={pagination}
          postsPerPage={postsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          fetchDetail={fetchDetail}
        />
      </div>
    </>
  );
}
